import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Status, History, Steps } from "./shared";

const FLOW = ["Submitted", "Immediate Head", "DCO Approval", "Link Sent"];
const IDX = { "For Immediate Head Approval": 1, "For DCO Approval": 2, "Link Sent": 4, "Link Expired": 4 };

export const CopyRequestForm = ({ doc, setDoc, onDone }) => {
  const [purpose, setPurpose] = useState("");
  useEffect(() => setPurpose(""), [doc]);
  const submit = async () => {
    try { const { data } = await api.post("/dms/copy", { library_id: doc.id, purpose }); toast.success(`${data.no} submitted to your Immediate Head`); setDoc(null); onDone(); } catch (e) { toast.error(e.response?.data?.detail || "Request failed"); }
  };
  return (
    <Dialog open={!!doc} onOpenChange={(v) => !v && setDoc(null)}>
      <DialogContent className="max-w-lg" data-testid="copy-form">
        <DialogHeader><DialogTitle>Document Copy Request</DialogTitle></DialogHeader>
        {doc && <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm"><span className="font-mono text-xs text-slate-400 mr-2">{doc.no}</span>{doc.title}</div>}
        <div className="space-y-1.5"><Label>Purpose</Label><Textarea value={purpose} onChange={(e) => setPurpose(e.target.value)} data-testid="copy-purpose" /></div>
        <DialogFooter><Button variant="outline" onClick={() => setDoc(null)}>Cancel</Button><Button onClick={submit} className="bg-blue-600 hover:bg-blue-700" data-testid="copy-submit">Submit</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CopyDetail = ({ c, setC, onDone }) => {
  const [reason, setReason] = useState("");
  const [days, setDays] = useState(7);
  const nav = useNavigate();
  useEffect(() => { setReason(""); setDays(7); }, [c]);
  if (!c) return null;
  const act = async (action) => {
    try { await api.post(`/dms/copy/${c.id}/action`, { action, reason, expiry_days: Number(days) }); toast.success("Updated"); setC(null); onDone(); } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };
  return (
    <Dialog open={!!c} onOpenChange={(v) => !v && setC(null)}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="copy-detail">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><span className="font-mono">{c.no}</span><Status s={c.status} /></DialogTitle></DialogHeader>
        <Steps steps={FLOW} current={IDX[c.status] ?? Math.max(1, (c.history || []).length - 1)} rejected={c.status === "Rejected"} />
        <div className="grid grid-cols-2 gap-3 text-sm">
          {[["Document", `${c.document_no} · ${c.title}`], ["Requestor", c.requestor_name], ["Company email", c.requestor_email], ["Immediate Head", c.head_name], ["Purpose", c.purpose], ["Link expires", c.expires_at && new Date(c.expires_at).toLocaleString()]].filter(([, v]) => v).map(([k, v]) => <div key={k}><div className="text-[10px] uppercase tracking-wide text-slate-400">{k}</div><div className="text-slate-800">{v}</div></div>)}
        </div>
        {c.token && c.status === "Link Sent" && <Button onClick={() => nav(`/documents/link/${c.token}`)} className="bg-emerald-600 hover:bg-emerald-700 w-fit" data-testid="copy-open-link"><Icons.Link className="h-4 w-4 mr-1.5" />Open my document link</Button>}
        {c.can_act && (
          <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-4 space-y-3" data-testid="copy-actions">
            {c.status === "For DCO Approval" && <div className="space-y-1.5 max-w-[200px]"><Label className="text-xs">Link expires after (days)</Label><Input type="number" min={1} max={90} value={days} onChange={(e) => setDays(e.target.value)} data-testid="copy-expiry" /></div>}
            <Textarea placeholder="Comment (required when rejecting)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="copy-reason" />
            <div className="flex gap-2"><Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => act("approve")} data-testid="copy-approve">{c.status === "For DCO Approval" ? "Approve & Send Link" : "Approve"}</Button><Button variant="outline" className="text-rose-600" onClick={() => act("reject")} data-testid="copy-reject">Reject</Button></div>
          </div>
        )}
        <div><div className="text-xs font-semibold text-slate-700 mb-2">History</div><History items={c.history} /></div>
      </DialogContent>
    </Dialog>
  );
};

export const CopyTab = ({ rows, reload }) => {
  const [cur, setCur] = useState(null);
  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">Only the Applicable DRC can request a copy. Open a posted document in the Library and click "Request a copy".</p>
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="copy-table">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500"><tr>{["DCR No.", "Document", "Requestor", "Immediate Head", "Status", ""].map((h) => <th key={h} className="px-3 py-2.5 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {!rows.length && <tr><td colSpan={6} className="text-center text-slate-400 py-10">No copy requests yet.</td></tr>}
            {rows.map((c) => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/70 cursor-pointer" onClick={() => setCur(c)} data-testid={`copy-row-${c.no}`}>
                <td className="px-3 py-2.5 font-mono text-xs">{c.no}</td><td className="px-3 py-2.5"><span className="font-medium text-slate-800">{c.title}</span><span className="ml-1.5 text-[10px] text-slate-400">{c.document_no}</span></td>
                <td className="px-3 py-2.5 text-xs">{c.requestor_name}</td><td className="px-3 py-2.5 text-xs">{c.head_name}</td><td className="px-3 py-2.5"><Status s={c.status} /></td>
                <td className="px-3 py-2.5 text-right">{c.can_act && <span className="rounded-full bg-blue-600 text-white px-2 py-0.5 text-[10px]">Action needed</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CopyDetail c={cur} setC={setCur} onDone={reload} />
    </div>
  );
};
